import {
    NoteDestination,
    extractResourceNoteEntityTypeResourceCategory,
} from '../../components/shared/Notes/utils/types';
import { useHasPermission } from '../../components/v2BaseComponents/Permissions/hooks';
import { PermissionDomains } from '../../components/v2BaseComponents/Permissions/types';
import { APITypes, Dynamo, MedicalDataTypes } from '../../primary-record-types';
import { isResourceNoteEntityType } from '../../components/shared/Notes/utils/types';
import { NoteQueryArgs } from './useNotes';

export type UseHasNotePermissionArgs = {
    action: 'view' | 'edit';
    note: APITypes.notesService.NoteDetails | null;
};

export const noteEntityTypeToPermissionDomain: Record<Dynamo.NoteEntityType, PermissionDomains | null> = {
    'medical-profile': null,
    user: null,
    'care-team-member': 'careTeam',
    condition: 'conditions',
    medication: 'medications',
    allergy: 'allergies',
    visit: 'visits',
    'care-team': 'careTeam',
    imaging: 'imaging',
    vaccination: 'vaccinations',
    procedure: 'procedures',
    lab: 'labs',
    'condition-resource': 'conditions',
    'medication-resource': 'medications',
    'allergy-resource': 'allergies',
    'visit-resource': 'visits',
    'procedure-resource': 'procedures',
    'vaccination-resource': 'vaccinations',
    'imaging-resource': 'imaging',
    'lab-resource': 'labs',
    organization: null,
    'organization-client-record': null,
};

const resourceCategories: MedicalDataTypes.PrimaryRecordResourceCategory[] = [
    'medication',
    'allergy',
    'condition',
    'visit',
    'vaccination',
    'procedure',
    'imaging',
    'lab',
];

export type UseNoteResourceCategoryReturnValue = {
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory | null;
    isLoading: boolean;
};

export type UseNoteResourceCategoryParams =
    | (Pick<APITypes.notesService.NoteDetails, 'entityType' | 'medicalProfileId'> & { entityId: string | null })
    | null;

/**
 * The note resource category input can take different inputs
 *  - evaluating a destination a note may be sent to
 *  - query params for a note screen
 *  - a note object
 *
 *  It's important that we handle empty inputs as well for when components will conditionally have the note data yet.
 *  This is the share evaluation logic for all of those cases
 */
const useEvaluateNoteResourceCategory = (
    entityType: Dynamo.NoteEntityType | null,
): UseNoteResourceCategoryReturnValue => {
    if (resourceCategories.includes(entityType as MedicalDataTypes.PrimaryRecordResourceCategory)) {
        return { resourceCategory: entityType as MedicalDataTypes.PrimaryRecordResourceCategory, isLoading: false };
    }

    if (entityType && isResourceNoteEntityType(entityType)) {
        return { resourceCategory: extractResourceNoteEntityTypeResourceCategory(entityType), isLoading: false };
    }

    return { resourceCategory: null, isLoading: false };
};

export const useNoteResourceCategory = (
    params: APITypes.notesService.NoteDetails | null,
): UseNoteResourceCategoryReturnValue => {
    return useEvaluateNoteResourceCategory(params?.entityType || null);
};

export const useNoteQueryArgsResourceCategory = (params: NoteQueryArgs | null): UseNoteResourceCategoryReturnValue => {
    return useEvaluateNoteResourceCategory(params?.entityType || null);
};

export const useNoteDestinationResourceCategory = (
    params: NoteDestination | null,
): UseNoteResourceCategoryReturnValue => {
    return useEvaluateNoteResourceCategory(params?.entityType || null);
};

export default function useHasNotePermission({ action, note }: UseHasNotePermissionArgs): boolean {
    const { resourceCategory } = useNoteResourceCategory(note);
    const getPermissionDomain = (): PermissionDomains | null => {
        if (!note) return null;

        if (resourceCategory) {
            return noteEntityTypeToPermissionDomain[resourceCategory];
        }

        return noteEntityTypeToPermissionDomain[note.entityType];
    };
    const permissionDomain: PermissionDomains | null = getPermissionDomain();
    const hasPermission = useHasPermission(note?.medicalProfileId || null, permissionDomain, action);

    if (!note) return false;

    // a note loaded for a user belongs to the user
    if (note.entityType === 'user') return true;

    // if a user was able to load a medical profile note, they have permission
    if (note.entityType === 'medical-profile') return true;

    return hasPermission;
}
